import { deleteSession } from "~/hooks/media/durable-audio-recorder-db";
import { logger } from "~/lib/logger";

// AIDEV-NOTE: Post-upload IndexedDB cleanup must never gate anything the
// server needs. On iOS WebKit the readwrite delete transaction routinely
// stalls while the tab is backgrounded and aborts on resume (Sep 2026: 121-min
// gaps between the object landing and upload-complete, false stuck-upload
// emails, duplicate ~85 MB re-uploads). So: bound the delete with a timeout,
// remember the id in localStorage, and finish the job on the next listing.
// Ids in this list are hidden from the recovery banner so a stalled delete
// can't be mistaken for an unfinished upload.

const PENDING_CLEANUP_STORAGE_KEY = "pc-durable-pending-cleanup";

/** Long enough for a healthy delete; short enough that a stalled one can't hold the user on the upload screen. */
export const LOCAL_CLEANUP_TIMEOUT_MS = 10_000;

export type LocalCleanupOutcome = "deleted" | "timed-out" | "failed";

function hasLocalStorage(): boolean {
    return typeof window !== "undefined" && "localStorage" in window;
}

export function readPendingCleanupIds(): string[] {
    if (!hasLocalStorage()) return [];
    try {
        const raw = window.localStorage.getItem(PENDING_CLEANUP_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === "string")
            : [];
    } catch {
        return [];
    }
}

function writePendingCleanupIds(ids: string[]): void {
    if (!hasLocalStorage()) return;
    try {
        if (ids.length === 0) {
            window.localStorage.removeItem(PENDING_CLEANUP_STORAGE_KEY);
        } else {
            window.localStorage.setItem(
                PENDING_CLEANUP_STORAGE_KEY,
                JSON.stringify(ids),
            );
        }
    } catch {
        // Private mode / quota — the reconcile-on-mount purge still covers us.
    }
}

export function markPendingCleanup(sessionId: string): void {
    const ids = readPendingCleanupIds();
    if (ids.includes(sessionId)) return;
    writePendingCleanupIds([...ids, sessionId]);
}

export function clearPendingCleanup(sessionId: string): void {
    const ids = readPendingCleanupIds();
    if (!ids.includes(sessionId)) return;
    writePendingCleanupIds(ids.filter((id) => id !== sessionId));
}

/**
 * Delete an uploaded recording's local copy without letting a stalled
 * IndexedDB transaction block the caller. The id is marked pending BEFORE the
 * delete starts and cleared only when the delete settles successfully — even
 * if that happens after the timeout fired.
 */
export async function cleanupUploadedLocalRecording(
    sessionId: string,
    deleteFn: (sessionId: string) => Promise<void> = (id) => deleteSession(id),
    timeoutMs: number = LOCAL_CLEANUP_TIMEOUT_MS,
): Promise<LocalCleanupOutcome> {
    markPendingCleanup(sessionId);

    const deletion = deleteFn(sessionId).then(
        () => {
            clearPendingCleanup(sessionId);
            return "deleted" as const;
        },
        (error: unknown) => {
            logger
                .withError(error as Error)
                .warn(
                    `Local recording cleanup failed; will retry on next load: localRecordingId=${sessionId}`,
                );
            return "failed" as const;
        },
    );

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<LocalCleanupOutcome>((resolve) => {
        timer = setTimeout(() => resolve("timed-out"), timeoutMs);
    });

    try {
        const outcome = await Promise.race([deletion, timeout]);
        if (outcome === "timed-out") {
            logger.warn(
                `Local recording cleanup timed out after ${timeoutMs}ms; will retry on next load: localRecordingId=${sessionId}`,
            );
        }
        return outcome;
    } finally {
        if (timer !== undefined) clearTimeout(timer);
    }
}

// AIDEV-NOTE: One in-flight sweep per tab. listRecoverableRecordings runs on
// mount, focus, and visibility change; stacking sweeps on a stalled IDB would
// only queue more doomed transactions.
let pendingSweep: Promise<string[]> | null = null;

/**
 * Retry every pending local delete. Never throws; returns the ids it cleared.
 * Fire-and-forget from listing code — a stalled sweep must not block the list.
 */
export function retryPendingCleanups(
    deleteFn: (sessionId: string) => Promise<void> = (id) => deleteSession(id),
): Promise<string[]> {
    if (pendingSweep) return pendingSweep;

    const ids = readPendingCleanupIds();
    if (ids.length === 0) return Promise.resolve([]);

    pendingSweep = (async () => {
        const cleared: string[] = [];
        for (const id of ids) {
            try {
                await deleteFn(id);
                clearPendingCleanup(id);
                cleared.push(id);
            } catch (error) {
                logger
                    .withError(error as Error)
                    .warn(
                        `Pending local recording cleanup failed again: localRecordingId=${id}`,
                    );
            }
        }
        return cleared;
    })().finally(() => {
        pendingSweep = null;
    });

    return pendingSweep;
}
